import React, { useState, useEffect, useRef } from 'react';
import paymentService from '../../../services/paymentService';
import './PaymentEditModal.scss';

/**
 * Universal Payment Edit Modal
 * Works for both PaymentReceivable and PaymentPayable
 */
const PaymentEditModal = ({
  isOpen,
  onClose,
  paymentData,
  paymentType = 'receivable',
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    status: '',
    paymentMethod: '',
    bank: '',
    routing: '',
    accountNumber: '',
    notes: '',
    paymentLink: '',
    totalAmount: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Статусы разные для каждого типа (pending исключен - только при создании)
  const receivableStatuses = ['invoiced', 'withheld', 'canceled', 'on Hold', 'received', 'partially received', 'pay today'];
  const payableStatuses = ['invoiced', 'withheld', 'canceled', 'on Hold', 'paid', 'partially paid', 'pay today'];
  const statuses = paymentType === 'receivable' ? receivableStatuses : payableStatuses;
  const paymentMethods = ['ACH', 'Wire', 'Check', 'Credit Card', 'Cash', 'Zelle', 'Factoring', 'Other'];

  // Initialize form data when payment data changes
  useEffect(() => {
    if (paymentData) {
      const loadData = paymentData.load || paymentData || {};
      let currentTotalAmount = null;
      
      if (paymentType === 'receivable') {
        currentTotalAmount = paymentData.totalAmount ?? paymentData.amount ?? null;
      } else {
        currentTotalAmount = paymentData.totalAmount ?? paymentData.grossAmount ?? paymentData.amount ?? null;
      }
      
      const formattedTotalAmount = currentTotalAmount !== null && currentTotalAmount !== undefined 
        ? (typeof currentTotalAmount === 'number' ? currentTotalAmount.toFixed(2) : String(currentTotalAmount))
        : '';
      
      if (paymentType === 'receivable') {
        setFormData({
          status: paymentData.status || 'pending',
          paymentMethod: loadData.paymentMethod || paymentData.paymentMethod || '',
          notes: paymentData.notes || '',
          paymentLink: paymentData.paymentLink || '',
          totalAmount: formattedTotalAmount
        });
      } else {
        setFormData({
          status: paymentData.status || 'pending',
          paymentMethod: loadData.paymentMethod || paymentData.paymentMethod || '',
          bank: paymentData.bank || paymentData.bankDetails?.bank || '',
          routing: paymentData.routing || paymentData.bankDetails?.routing || '',
          accountNumber: paymentData.accountNumber || paymentData.bankDetails?.accountNumber || '',
          notes: paymentData.notes || '',
          totalAmount: formattedTotalAmount
        });
      }
      setError('');
    }
  }, [paymentData, paymentType]);

  if (!isOpen || !paymentData) return null;

  // Extract data for display
  const load = paymentData.load || paymentData || {};
  const customer = paymentData.customer || {};
  const carrier = paymentData.carrier || {};

  // Calculate days on hold display
  const getDaysOnHoldDisplay = () => {
    const daysOnHold = paymentData.daysOnHold;
    if (daysOnHold === null || daysOnHold === undefined) return null;
    
    const isPayed = paymentType === 'receivable' 
      ? paymentData.status === 'received' 
      : paymentData.status === 'paid';
    return {
      days: daysOnHold,
      label: isPayed ? 'Total days' : 'Days waiting',
      isPayed
    };
  };

  const daysOnHoldData = getDaysOnHoldDisplay();

  const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (typeof value === 'number') {
      return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    const cleaned = String(value).replace(/[$,\s]/g, '');
    const numeric = Number(cleaned);
    if (!Number.isNaN(numeric) && cleaned !== '') {
      return `$${numeric.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return String(value);
  };

  const getOriginalRate = () => {
    if (paymentType === 'receivable') {
      return load.customerRate ?? paymentData.customerRate ?? null;
    }
    return load.carrierRate ?? paymentData.carrierRate ?? null;
  };

  const getTotalRate = () => {
    if (paymentType === 'receivable') {
      return paymentData.totalAmount ?? paymentData.amount ?? null;
    }
    return paymentData.totalAmount ?? paymentData.grossAmount ?? paymentData.amount ?? null;
  };

  // Format status for display
  const formatStatus = (status) => {
    if (!status) return 'N/A';
    return status
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Handle form field change
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    // Filter only PDF files
    const pdfFiles = files.filter(file => file.type === 'application/pdf');
    setSelectedFiles(prev => [...prev, ...pdfFiles]);
  };

  // Remove selected file
  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle save
  const handleSave = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const paymentId = paymentData._id || paymentData.id;
      if (!paymentId) {
        setError('Invalid payment ID');
        setIsSubmitting(false);
        return;
      }

      // Validate required fields
      if (!formData.paymentMethod || formData.paymentMethod.trim() === '') {
        setError('Payment Method is required');
        setIsSubmitting(false);
        return;
      }

      // Validate notes for partially paid/received status
      if (paymentType === 'payable' && formData.status === 'partially paid') {
        if (!formData.notes || formData.notes.trim() === '') {
          setError('Payment notes are required when status is "partially paid"');
          setIsSubmitting(false);
          return;
        }
      }
      if (paymentType === 'receivable' && formData.status === 'partially received') {
        if (!formData.notes || formData.notes.trim() === '') {
          setError('Payment notes are required when status is "partially received"');
          setIsSubmitting(false);
          return;
        }
      }

      const currentTotalAmount = paymentData.totalAmount ?? paymentData.amount ?? paymentData.grossAmount ?? null;
      const newTotalAmount = formData.totalAmount && formData.totalAmount.trim() !== '' 
        ? parseFloat(formData.totalAmount) 
        : null;

      if (newTotalAmount !== null && (isNaN(newTotalAmount) || newTotalAmount < 0)) {
        setError('Total Amount must be a valid positive number');
        setIsSubmitting(false);
        return;
      }

      const updateData = {};
      
      if (formData.status !== (paymentData.status || 'pending')) {
        updateData.status = formData.status;
      }
      
      updateData.paymentMethod = formData.paymentMethod;
      
      if (newTotalAmount !== null && newTotalAmount !== currentTotalAmount) {
        updateData.totalAmount = newTotalAmount;
      }
      
      if (paymentType === 'receivable') {
        if (formData.notes !== (paymentData.notes || '')) {
          updateData.notes = formData.notes;
        }
        
        if (formData.paymentLink !== (paymentData.paymentLink || '')) {
          updateData.paymentLink = formData.paymentLink;
        }
      } else {
        if (formData.bank !== (paymentData.bank || '')) {
          updateData.bank = formData.bank;
        }
        
        if (formData.routing !== (paymentData.routing || '')) {
          updateData.routing = formData.routing;
        }
        
        if (formData.accountNumber !== (paymentData.accountNumber || '')) {
          updateData.accountNumber = formData.accountNumber;
        }
        
        if (formData.notes !== (paymentData.notes || '')) {
          updateData.notes = formData.notes;
        }
      }

      // Check if we have files to upload or data to update
      const hasDataChanges = Object.keys(updateData).length > 0;
      const hasFiles = selectedFiles.length > 0;

      if (!hasDataChanges && !hasFiles) {
        onClose();
        return;
      }

      // If we have files, use FormData
      if (hasFiles) {
        const formDataToSend = new FormData();
        
        // Add files
        selectedFiles.forEach(file => {
          formDataToSend.append('pdfs', file);
        });
        
        // Add other fields
        Object.keys(updateData).forEach(key => {
          formDataToSend.append(key, updateData[key]);
        });

        if (paymentType === 'receivable') {
          await paymentService.updateReceivableWithFiles(paymentId, formDataToSend);
        } else {
          await paymentService.updatePayableWithFiles(paymentId, formDataToSend);
        }
      } else {
        // No files, just update data
        if (paymentType === 'receivable') {
          await paymentService.updateReceivable(paymentId, updateData);
        } else {
          await paymentService.updatePayable(paymentId, updateData);
        }
      }

      setSelectedFiles([]);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating payment:', err);
      setError(
        err?.response?.data?.error || 
        err?.response?.data?.message || 
        err?.message || 
        'Failed to update payment'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="payment-edit-modal__backdrop" onClick={handleBackdropClick}>
      <div className="payment-edit-modal">
        {/* Header */}
        <div className="payment-edit-modal__header">
          <h2 className="payment-edit-modal__title">
            Edit Accounts {paymentType === 'receivable' ? 'Receivable' : 'Payable'} 
          </h2>
          <button 
            className="payment-edit-modal__close-btn" 
            onClick={onClose}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="payment-edit-modal__content">
          {/* Days on Hold Counter - Prominent Display */}
          {daysOnHoldData && (
            <div className={`payment-edit-modal__days-counter ${daysOnHoldData.isPayed ? 'is-completed' : ''}`}>
              <div className="payment-edit-modal__days-value">{daysOnHoldData.days}</div>
              <div className="payment-edit-modal__days-label">{daysOnHoldData.label}</div>
              {paymentData.statusChangedAt && (
                <div className="payment-edit-modal__days-dates">
                  Started: {new Date(paymentData.statusChangedAt).toLocaleDateString()}
                  {paymentData.payedDate && (
                    <> • Completed: {new Date(paymentData.payedDate).toLocaleDateString()}</>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Load Information */}
          <div className="payment-edit-modal__section">
            <h3 className="payment-edit-modal__section-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="3" width="15" height="13" rx="2" />
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                <circle cx="5.5" cy="18.5" r="2.5" />
                <circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
              Load Information
            </h3>
            <div className="payment-edit-modal__info-grid">
              <div className="payment-edit-modal__info-item">
                <span className="payment-edit-modal__info-label">Order ID</span>
                <span className="payment-edit-modal__info-value payment-edit-modal__info-value--highlight">
                  {load.OrderId || paymentData.OrderId || load.orderId || paymentData.orderId || 'N/A'}
                </span>
              </div>
              <div className="payment-edit-modal__info-item">
                <span className="payment-edit-modal__info-label">
                  {paymentType === 'receivable' ? 'Original Customer Rate' : 'Original Carrier Rate'}
                </span>
                <span className="payment-edit-modal__info-value payment-edit-modal__info-value--money">
                  {formatMoney(getOriginalRate())}
                </span>
              </div>
              <div className="payment-edit-modal__info-item">
                <span className="payment-edit-modal__info-label">Total Amount</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.25rem' }}>
                  <span className="payment-edit-modal__info-value payment-edit-modal__info-value--money" style={{ fontSize: '0.75rem', color: 'var(--theme-text-secondary)', fontWeight: 500 }}>
                    Current: {formatMoney(getTotalRate())}
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    className="payment-edit-modal__input payment-edit-modal__input--money"
                    value={formData.totalAmount || ''}
                    onChange={(e) => handleChange('totalAmount', e.target.value)}
                    placeholder={`Enter new amount (current: ${formatMoney(getTotalRate())})`}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div className="payment-edit-modal__info-item">
                <span className="payment-edit-modal__info-label">Load Status</span>
                <span className={`payment-edit-modal__status-badge status-${((load.status || paymentData.loadStatus || paymentData.status || 'unknown')).toLowerCase()}`}>
                  {formatStatus(load.status || paymentData.loadStatus || paymentData.status)}
                </span>
              </div>
              {(load.paymentMethod || load.paymentTerms) && (
                <div className="payment-edit-modal__info-item payment-edit-modal__info-item--full">
                  <div className="payment-edit-modal__info-row">
                    {load.paymentMethod && (
                      <div className="payment-edit-modal__info-item-inline">
                        <span className="payment-edit-modal__info-label">Payment Method</span>
                        <span className="payment-edit-modal__info-value">
                          {load.paymentMethod}
                        </span>
                      </div>
                    )}
                    {load.paymentTerms && (
                      <div className="payment-edit-modal__info-item-inline">
                        <span className="payment-edit-modal__info-label">DTP</span>
                        <span className="payment-edit-modal__info-value">
                          Net {load.paymentTerms}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Customer/Carrier Information */}
          <div className="payment-edit-modal__section">
            <h3 className="payment-edit-modal__section-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              {paymentType === 'receivable' ? 'Customer' : 'Carrier'} Information
            </h3>
            
            {paymentType === 'receivable' ? (
              <>
                {/* Company Information Section */}
                <div className="payment-edit-modal__info-grid">
                  <div className="payment-edit-modal__info-item payment-edit-modal__info-item--full">
                    <span className="payment-edit-modal__info-label">Company Name</span>
                    <span className="payment-edit-modal__info-value payment-edit-modal__info-value--highlight">
                      {customer.companyName || 'N/A'}
                    </span>
                  </div>
                  {customer.email && (
                    <div className="payment-edit-modal__info-item">
                      <span className="payment-edit-modal__info-label">Email</span>
                      <span className="payment-edit-modal__info-value">
                        {customer.email}
                      </span>
                    </div>
                  )}
                  {customer.phoneNumber && (
                    <div className="payment-edit-modal__info-item">
                      <span className="payment-edit-modal__info-label">Phone</span>
                      <span className="payment-edit-modal__info-value">
                        {customer.phoneNumber}
                      </span>
                    </div>
                  )}
                  {customer.customerAddress && (
                    <div className="payment-edit-modal__info-item payment-edit-modal__info-item--full">
                      <span className="payment-edit-modal__info-label">Address</span>
                      <span className="payment-edit-modal__info-value">
                        {[
                          customer.customerAddress.address,
                          customer.customerAddress.city,
                          customer.customerAddress.state,
                          customer.customerAddress.zipCode
                        ].filter(Boolean).join(', ') || 'N/A'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Payment Information Section */}
                {(customer.paymentMethod || customer.paymentTerms || (customer.creditLimit !== undefined && customer.creditLimit !== null)) && (
                  <div className="payment-edit-modal__info-grid payment-edit-modal__info-grid--payment" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #e2e8f0' }}>
                    <div className="payment-edit-modal__info-item payment-edit-modal__info-item--full" style={{ marginBottom: '0.5rem' }}>
                      <span className="payment-edit-modal__info-label" style={{ fontSize: '0.8125rem', fontWeight: '700', color: '#1D75BF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Payment Information
                      </span>
                    </div>
                   <div className="payment-edit-modal__info-payment-details">
                    
                    {customer.paymentTerms && (
                      <div className="payment-edit-modal__info-item payment-edit-modal__info-item--full">
                        <span className="payment-edit-modal__info-label">DTP</span>
                        <span className="payment-edit-modal__info-value" style={{ lineHeight: '1.5' }}>
                          Net {customer.paymentTerms}
                        </span>
                      </div>
                    )}
                     {customer.creditLimit !== undefined && customer.creditLimit !== null && (
                      <div className="payment-edit-modal__info-item">
                        <span className="payment-edit-modal__info-label">Credit Limit</span>
                        <span className="payment-edit-modal__info-value ">
                          ${typeof customer.creditLimit === 'number' 
                            ? customer.creditLimit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : parseFloat(customer.creditLimit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          }
                        </span>
                      </div>
                    )}
                   </div>
                  </div>
                )}
              </>
            ) : (
              <div className="payment-edit-modal__info-grid">
                <div className="payment-edit-modal__info-item payment-edit-modal__info-item--full">
                  <span className="payment-edit-modal__info-label">Company Name</span>
                  <span className="payment-edit-modal__info-value">
                    {carrier.companyName || carrier.name || 'N/A'}
                  </span>
                </div>
                {carrier.email && (
                  <div className="payment-edit-modal__info-item">
                    <span className="payment-edit-modal__info-label">Email</span>
                    <span className="payment-edit-modal__info-value">
                      {carrier.email}
                    </span>
                  </div>
                )}
                {carrier.phoneNumber && (
                  <div className="payment-edit-modal__info-item">
                    <span className="payment-edit-modal__info-label">Phone</span>
                    <span className="payment-edit-modal__info-value">
                      {carrier.phoneNumber}
                    </span>
                  </div>
                )}
                {carrier.mcNumber && (
                  <div className="payment-edit-modal__info-item">
                    <span className="payment-edit-modal__info-label">MC Number</span>
                    <span className="payment-edit-modal__info-value">
                      {carrier.mcNumber}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Editable Fields Section */}
          <div className="payment-edit-modal__section payment-edit-modal__section--form">
            <h3 className="payment-edit-modal__section-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Payment Details
            </h3>

            <div className="payment-edit-modal__form">
              {/* Status & Payment Method Row */}
              <div className="payment-edit-modal__form-row">
                <div className="payment-edit-modal__form-group">
                  <label className="payment-edit-modal__label">Status</label>
                  <select
                    className="payment-edit-modal__select"
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                  >
                    {/* Показываем pending только если текущий статус pending, но делаем его disabled */}
                    {formData.status === 'pending' && (
                      <option value="pending" disabled>Pending</option>
                    )}
                    {statuses.map(status => {
                      const formatStatus = (s) => {
                        if (s === 'on Hold') return 'On Hold';
                        if (s === 'canceled') return 'Canceled';
                        if (s === 'partially paid') return 'Partially Paid';
                        if (s === 'partially received') return 'Partially Received';
                        if (s === 'pay today') return 'Pay Today';
                        return s.charAt(0).toUpperCase() + s.slice(1);
                      };
                      return (
                        <option key={status} value={status}>
                          {formatStatus(status)}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="payment-edit-modal__form-group">
                  <label className="payment-edit-modal__label">
                    Payment Method <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    className="payment-edit-modal__select"
                    value={formData.paymentMethod}
                    onChange={(e) => handleChange('paymentMethod', e.target.value)}
                    required
                  >
                    <option value="">Select method</option>
                    {paymentMethods.map(method => (
                      <option key={method} value={method}>{method}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Payment Notes and Payment Link for Receivable */}
              {paymentType === 'receivable' ? (
                <>
                  {/* Payment Link - только для Credit Card */}
                  {formData.paymentMethod === 'Credit Card' && (
                    <div className="payment-edit-modal__form-group">
                      <label className="payment-edit-modal__label">Payment Link</label>
                      <input
                        type="text"
                        className="payment-edit-modal__input"
                        value={formData.paymentLink}
                        onChange={(e) => handleChange('paymentLink', e.target.value)}
                        placeholder="Enter payment link URL"
                      />
                    </div>
                  )}
                  
                  {/* Payment Notes */}
                  <div className="payment-edit-modal__form-group">
                    <label className="payment-edit-modal__label">
                      Payment Notes
                      {formData.status === 'partially received' && (
                        <span style={{ color: '#ef4444' }}> *</span>
                      )}
                    </label>
                    <textarea
                      className="payment-edit-modal__textarea"
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      placeholder="Enter payment notes"
                      rows={4}
                      required={formData.status === 'partially received'}
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Bank Details for Payable */}
                  <div className="payment-edit-modal__form-row">
                    <div className="payment-edit-modal__form-group">
                      <label className="payment-edit-modal__label">Bank Name</label>
                      <input
                        type="text"
                        className="payment-edit-modal__input"
                        value={formData.bank}
                        onChange={(e) => handleChange('bank', e.target.value)}
                        placeholder="Enter bank name"
                      />
                    </div>
                    
                    <div className="payment-edit-modal__form-group">
                      <label className="payment-edit-modal__label">Routing Number</label>
                      <input
                        type="text"
                        className="payment-edit-modal__input"
                        value={formData.routing}
                        onChange={(e) => handleChange('routing', e.target.value)}
                        placeholder="Enter routing number"
                      />
                    </div>
                  </div>

                  {/* Account Number */}
                  <div className="payment-edit-modal__form-group">
                    <label className="payment-edit-modal__label">Account Number</label>
                    <input
                      type="text"
                      className="payment-edit-modal__input"
                      value={formData.accountNumber}
                      onChange={(e) => handleChange('accountNumber', e.target.value)}
                      placeholder="Enter account number"
                    />
                  </div>

                  {/* Payment Notes */}
                  <div className="payment-edit-modal__form-group">
                    <label className="payment-edit-modal__label">
                      Payment Notes
                      {formData.status === 'partially paid' && (
                        <span style={{ color: '#ef4444' }}> *</span>
                      )}
                    </label>
                    <textarea
                      className="payment-edit-modal__textarea"
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      placeholder="Enter payment notes"
                      rows={4}
                      required={formData.status === 'partially paid'}
                    />
                  </div>
                </>
              )}

              {/* File Upload */}
              <div className="payment-edit-modal__form-group">
                <label className="payment-edit-modal__label">Attach PDF Files</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf"
                  multiple
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="payment-edit-modal__upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17,8 12,3 7,8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Choose PDF Files
                </button>

                {/* Selected Files List */}
                {selectedFiles.length > 0 && (
                  <div className="payment-edit-modal__files-list">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="payment-edit-modal__file-item">
                        <span className="payment-edit-modal__file-icon">📕</span>
                        <span className="payment-edit-modal__file-name">{file.name}</span>
                        <button
                          type="button"
                          className="payment-edit-modal__file-remove"
                          onClick={() => handleRemoveFile(index)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="payment-edit-modal__error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="payment-edit-modal__footer">
          <button
            className="payment-edit-modal__btn payment-edit-modal__btn--cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="payment-edit-modal__btn payment-edit-modal__btn--save"
            onClick={handleSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="payment-edit-modal__spinner"></span>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentEditModal;

